import DOMPurify from "dompurify";

// HTML-only profile: none of our rendered HTML needs inline SVG/MathML, and dropping
// those namespaces removes the bulk of the known mXSS bypass surface.
const CONFIG = {
  USE_PROFILES: { html: true },
  // target is not in DOMPurify's default attribute allowlist, but our i18n texts and
  // admin-authored descriptions rely on target="_blank" (browsers imply rel=noopener).
  ADD_ATTR: ["target"],
  FORBID_TAGS: ["style", "form", "input", "button", "select", "textarea", "option"],
};

/**
 * Sanitizes HTML before it is handed to dangerouslySetInnerHTML.
 * Keeps the formatting tags our i18n texts and admin-authored descriptions rely on
 * (b, strong, i, em, br, p, ul/ol/li, a, img, table, headings, span/div with class/style/title)
 * and strips scripts, event handlers and javascript: URLs.
 */
export const sanitizeHtml = (html: string | Node | null | undefined): string =>
  html ? DOMPurify.sanitize(html, CONFIG) : "";

/**
 * Same profile as sanitizeHtml, but returns nodes instead of a string so callers
 * can append the result (replaceChildren/appendChild) rather than assigning HTML
 * back into the DOM through innerHTML.
 */
export const sanitizeHtmlFragment = (
  html: string | Node | null | undefined
): DocumentFragment =>
  DOMPurify.sanitize(html ?? "", { ...CONFIG, RETURN_DOM_FRAGMENT: true });

/**
 * SVG profile for map marker/cluster icons: the HTML-only profile above strips
 * <svg> entirely. Keeps the shape/text tags and the id, transform, fill and
 * font attributes the marker styling relies on; drops scripts, event handlers
 * and <use> references.
 */
export const sanitizeSvgFragment = (svg: string): DocumentFragment =>
  DOMPurify.sanitize(svg, {
    USE_PROFILES: { svg: true, svgFilters: true },
    RETURN_DOM_FRAGMENT: true,
  });
